import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { OperatingCostCategory, OperatingCostCategoryData } from '../types/operatingCosts';

interface OperatingCostCategoriesContextValue {
    categories: OperatingCostCategory[];
    loading: boolean;
    refresh: () => Promise<void>;
}

const OperatingCostCategoriesContext = createContext<OperatingCostCategoriesContextValue | undefined>(undefined);

export const useOperatingCostCategories = () => {
    const context = useContext(OperatingCostCategoriesContext);
    if (!context) {
        throw new Error('useOperatingCostCategories must be used within an OperatingCostCategoriesProvider');
    }
    return context;
};

export const OperatingCostCategoriesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [categories, setCategories] = useState<OperatingCostCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

    const fetchCategories = useCallback(async () => {
        try {
            const db = getDatabase();
            const snapshot = await get(ref(db, 'config/operatingCostCategories'));

            if (snapshot.exists()) {
                const data = snapshot.val() as Record<string, OperatingCostCategoryData>;
                const parsed: OperatingCostCategory[] = Object.entries(data)
                    .map(([key, val]) => ({
                        key,
                        label: val.label,
                        sortOrder: val.sortOrder
                    }))
                    .sort((a, b) => {
                        // First sort by sortOrder if both have it
                        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
                            return a.sortOrder - b.sortOrder;
                        }
                        // Items with sortOrder come before those without
                        if (a.sortOrder !== undefined) return -1;
                        if (b.sortOrder !== undefined) return 1;
                        // Fall back to alphabetical Swedish collation
                        return a.label.localeCompare(b.label, 'sv');
                    });
                setCategories(parsed);
            } else {
                setCategories([]);
            }
        } catch (err) {
            console.error('Error fetching operating cost categories:', err);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (isAuthenticated === null) return;
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchCategories();
    }, [isAuthenticated, fetchCategories]);

    const refresh = useCallback(async () => {
        await fetchCategories();
    }, [fetchCategories]);

    const value = useMemo(() => ({
        categories,
        loading,
        refresh,
    }), [categories, loading, refresh]);

    return (
        <OperatingCostCategoriesContext.Provider value={value}>
            {children}
        </OperatingCostCategoriesContext.Provider>
    );
};
